/** SA-1080 (spec §5.1): the short form that adds one kind to the policy. It holds its own text;
 *  nothing reaches the draft until Add kind is pressed, and then only the one new kind. */
import { useEffect, useState } from 'react'
import { Button, Input } from '@/components/ui'
import type { MailboxHandler } from '@/services/mailboxes'
import { AREA, Field, HandlerFields } from './policyEditorParts'
import { addKindRefusal, kindIdFromLabel, type KindDoc, type PolicyDoc } from './routerPolicyModel'

type NewKind = Pick<KindDoc, 'label' | 'description' | 'handler' | 'handler_config'>

export interface AddKindFormProps {
  doc: PolicyDoc
  handlers: MailboxHandler[]
  offered: MailboxHandler[]
  agendas: Array<{ workflow_id: string; name: string }>
  workflows: Array<{ id: string; name: string }>
  onAdd: (id: string, fields: NewKind) => void
  onCancel: () => void
  onTypedChange: (typed: boolean) => void
}

export function AddKindForm({ doc, handlers, offered, agendas, workflows, onAdd, onCancel, onTypedChange }: AddKindFormProps) {
  const [label, setLabel] = useState('')
  const [typedId, setTypedId] = useState('')
  const [idTouched, setIdTouched] = useState(false)
  const [description, setDescription] = useState('')
  const [handler, setHandler] = useState('')
  const [config, setConfig] = useState<Record<string, unknown>>({})
  const id = idTouched ? typedId : kindIdFromLabel(label)
  const typed = [label, typedId, description, handler].some((v) => v !== '')
  useEffect(() => { onTypedChange(typed) }, [typed, onTypedChange])
  useEffect(() => () => onTypedChange(false), [onTypedChange])
  const refusal = addKindRefusal(doc, { id, label, description, handler })

  return (
    <div role="group" aria-label="New kind" className="rounded-md border border-border p-3 space-y-3">
      <div className="grid grid-cols-1 @md:grid-cols-3 gap-3">
        <Field label="Label">
          {(fid) => <Input id={fid} value={label} onChange={(e) => setLabel(e.target.value)} />}
        </Field>
        <Field label="Id">
          {(fid) => <Input id={fid} value={id} onChange={(e) => { setIdTouched(true); setTypedId(e.target.value) }} />}
        </Field>
        <HandlerFields prefix="new" entry={{ handler, handler_config: config }}
          onChange={(c) => { if (c.handler !== undefined) setHandler(c.handler); if (c.handler_config !== undefined) setConfig(c.handler_config) }}
          handlers={handlers} offered={offered} agendas={agendas} workflows={workflows} at={() => null} />
      </div>
      <Field label="Description (what the classifier reads)">
        {(fid) => <textarea id={fid} rows={2} className={AREA} value={description} onChange={(e) => setDescription(e.target.value)} />}
      </Field>
      {refusal && <p className="text-xs text-muted-foreground">{refusal}</p>}
      <div className="flex items-center gap-2">
        <Button type="button" size="sm" disabled={refusal !== null}
          onClick={() => onAdd(id, { label, description, handler, handler_config: config })}>Add kind</Button>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  )
}
